import styles from './index.module.css'

import { LuExternalLink } from '@react-icons/all-files/lu/LuExternalLink'
import React from 'react'
import { Link } from 'react-router-dom'

import { TEAM_LIBRARY } from '@disco/constants/GroupTypes'
import { SEARCH_RESULTS_PANE } from '@disco/constants/PaneTypes'
import { TRACK_VIEW } from '@disco/constants/ViewTypes'
import type { AppliedSearch } from '@disco/state-management/reducers/search'
import {
  clearResults,
  clearSearch,
  fetchResults,
  selectBrowseFilterState,
  selectSearchPage,
  setAiMode,
  setQuery,
  setSorting,
} from '@disco/state-management/reducers/search-page'
import { mapGlobalSearchToSearchPage } from '@disco/utils/global-search'

import { Box, DiscoCallout } from 'components/ds'

import { useDispatch, useSelector } from 'hooks/use-redux'

type BannerPane = {
  type?: string
  view?: string | null
  groupId?: number | null
  simSearch?: unknown
}

export const shouldShowViewInTableFormatBanner = (
  pane: BannerPane,
  groupType?: string,
): boolean => {
  if (pane.view !== TRACK_VIEW || pane.simSearch) return false

  const isBrowseTracks =
    groupType === TEAM_LIBRARY && typeof pane.groupId === 'number'
  const isGlobalSearchTracks =
    pane.type === SEARCH_RESULTS_PANE && typeof pane.groupId !== 'number'

  return isBrowseTracks || isGlobalSearchTracks
}

type Props = {
  useLegacySearch: boolean
}

const EMPTY_APPLIED_SEARCH: AppliedSearch = {
  searchTerm: '',
  groups: [],
  groupObjects: [],
  tags: [],
  nativeTags: [],
  filters: [],
  startDate: new Date(0),
  endDate: null,
  duration: null,
  filterState: null,
}

const ViewInTableFormatBanner = ({ useLegacySearch }: Props) => {
  const dispatch = useDispatch()
  const legacySearch = useSelector(state => state.search)
  const initialFetchLoaded = useSelector(
    state => selectSearchPage(state).initialFetchLoaded,
  )
  const browseFilterState = useSelector(selectBrowseFilterState)

  const handleClick = () => {
    const source =
      (useLegacySearch && legacySearch.appliedSearch) || EMPTY_APPLIED_SEARCH
    const transfer = mapGlobalSearchToSearchPage({
      searchTerm: source.searchTerm,
      groups: source.groups,
      tags: source.tags,
      nativeTags: source.nativeTags,
      filters: source.filters,
      startDate: source.startDate ?? null,
      endDate: source.endDate ?? null,
      duration: source.duration ?? null,
      metadataKeys: useLegacySearch ? legacySearch.metadataKeys : [],
      groupObjects: source.groupObjects,
    })

    const filters = useLegacySearch
      ? (source.filterState ?? transfer.filters)
      : (browseFilterState ?? [])
    dispatch(clearSearch(filters))
    dispatch(setQuery(transfer.query))
    dispatch(setSorting([]))
    dispatch(setAiMode('off'))
    dispatch(clearResults())

    // The page fetches on first mount; subsequent visits need an explicit refresh.
    if (initialFetchLoaded) dispatch(fetchResults())
  }

  return (
    <Box px="2" pt="2">
      <DiscoCallout type="feature" variant="soft" size="1">
        <Link
          to="/search"
          onClick={handleClick}
          className={styles.tableFormatLink}
        >
          View in table format
          <LuExternalLink aria-hidden="true" size={16} />
        </Link>
      </DiscoCallout>
    </Box>
  )
}

export default ViewInTableFormatBanner
